import moment from "moment";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Button from "src/components/Button";
import Card from "src/components/Card";
import SubscriptionFeedbackForm, {
	FeedbackValue,
} from "src/components/forms/SubscriptionFeedbackForm";
import Layout from "src/components/layout/Layout";
import Link from "src/components/Link";
import useTenant from "src/hooks/api/services/tenants/useTenant";
import useFetchProfile from "src/hooks/api/useFetchProfile";
import usePost from "src/hooks/rest/usePost";
import useSubscription from "src/hooks/selectors/useSubscription";
import useAlert from "src/hooks/useAlert";
import useRedirectToCustomerPortal from "src/hooks/useRedirectToCustomerPortal";

export default function PausePlanView() {
	const { t } = useTranslation("payment-view", {
		keyPrefix: "pause-plan-view",
	});
	const { tenant } = useTenant();
	const { isLoading } = useRedirectToCustomerPortal();
	const [sendAlert] = useAlert();
	const [pauseSubscription, loading] = usePost(
		"/auth/subscriptions/plans/pause"
	);
	const navigate = useNavigate();
	const [feedback, setFeedback] = useState<FeedbackValue>({
		platform: {
			rating: 0,
			feedback: "",
		},
		contact: {
			rating: 0,
			feedback: "",
		},
		return_period: "",
		reason: "",
	});
	const subscription = useSubscription();

	const { loading: fetchingProfile, fetchProfile } = useFetchProfile();

	const isFormValid = () => {
		return (
			feedback.platform.feedback &&
			feedback.contact.feedback &&
			feedback.return_period &&
			feedback.reason
		);
	};

	const handlePause = () => {
		sendAlert({
			type: "confirm",
			title: t("confirm.title"),
			text: t("confirm.description"),
			onConfirm: () => {
				pauseSubscription({
					feedback,
				})
					.then(() => {
						fetchProfile().then(() => {
							navigate(`/dashboard?paused=1`);
						});
					})
					.catch(() => {
						sendAlert({
							title: t("error.title"),
							text: t("error.description"),
						});
					});
			},
		});
	};

	if (!subscription.can_cancel || subscription.cancelable_at) {
		return (
			<Layout
				className="dashboard-profile"
				container="xxl"
				loading={isLoading}
				title={t("title")}
			>
				<Layout.BackButton
					to="/profile/billing/change"
					label={t("back")}
				/>
				<div className="flex flex-col justify-center items-center flex-1">
					<Card contentClassName="text-background-foreground max-w-[620px]">
						<h2 className="mb-4">{t("not-cancelable.title")}</h2>
						<p className="mb-0">
							<Trans
								i18nKey="not-cancelable.description"
								{...{ t }}
							>
								Dit abonnement kan je niet zelf pauzeren. Neem
								contact op via
								<a
									className="underline"
									href={`mailto: support@${tenant?.urls?.domain}`}
								>
									support@{tenant?.urls?.domain}
								</a>
							</Trans>
						</p>
					</Card>
				</div>
			</Layout>
		);
	}

	return (
		<Layout
			className="dashboard-profile"
			container="xxl"
			loading={isLoading}
			title={t("title")}
		>
			<Layout.BackButton to="/profile/billing/change" label={t("back")} />
			<div className="flex flex-col justify-center items-center flex-1">
				<Card contentClassName="text-background-foreground max-w-[620px] flex flex-col gap-4">
					{!subscription?.is_paused ? (
						<>
							<div className="flex flex-col gap-2">
								<h2 className="mb-4">{t("form.title")}</h2>
								<p>{t("form.description")}</p>
							</div>

							<SubscriptionFeedbackForm
								{...{ feedback }}
								onChange={setFeedback}
							/>

							<div>
								<span>
									{t("form.disclaimer", {
										date: moment(
											subscription.ends_at
										).format("DD-MM-YYYY"),
									})}
								</span>
								<small>
									<Trans
										i18nKey="form.current-usage"
										{...{ t }}
									>
										Dit bedrag wordt aan het eind van je
										abonnement afgeschreven
										<Link
											className="underline"
											to="/profile/billing/upcoming"
										>
											Huidige verbruik bekijken
										</Link>
									</Trans>
								</small>

								{subscription?.plan?.slug === "basic-small" && (
									<small className="mt-2 block text-red">
										<strong>{t("form.old-prices")}</strong>
									</small>
								)}
							</div>
							<div className="flex mt-8 gap-4 justify-between items-center">
								<Button
									disabled={loading}
									to="/profile/billing/change"
									type="ghost"
								>
									{t("form.cancel")}
								</Button>
								<Button
									type="green"
									disabled={!isFormValid()}
									loading={loading || fetchingProfile}
									onClick={handlePause}
								>
									{t("form.pause")}
								</Button>
							</div>
						</>
					) : (
						<div className="flex flex-col gap-2">
							<h2 className="mb-4">
								{t("already-paused.title")}
							</h2>
							<p>{t("already-paused.description")}</p>
						</div>
					)}
				</Card>
			</div>
		</Layout>
	);
}
